/** Actions whose bespoke control (see `ActionCustomUI`) already owns the
 *  `execute/` click, so the generic run-style button in `PreflightItemActionBar`
 *  stands down for them.
 *
 *  Two buttons firing the same call from two places on one screen — the row's
 *  Run and the detail body's "Create CNAME record" — is worse than one, so an
 *  action earns a place here only when its own control does something the
 *  generic button CANNOT:
 *
 *    * it sends values with the call (access-group arguments, sync params), so
 *      a bare execute would run the action with nothing to work from;
 *    * it withholds the click on live state the action bar cannot see (Jira or
 *      Twilio not configured on the server, no fleet system yet, the review
 *      guest already created);
 *    * it refetches its own live probe afterwards — a query the generic
 *      invalidation (preflight items + run logs) does not touch, so the panel
 *      would keep showing pre-run state.
 *
 *  A control whose button is an unconditional `items.execute(topId, item.id)`
 *  belongs nowhere near this list: drop that button and let the generic one do
 *  it (EMPLOYEE_ROLES did exactly that).
 */
export const ACTIONS_OWNING_EXECUTE: ReadonlySet<string> = new Set([
  // Posts `materialize/` with the per-alias access tags staged in the control.
  'ACCESS_GROUPS_SETUP',
  // Stage the sync form's params on the item, then execute (see `useItemLaunch`).
  'CATALOG_SYNC',
  'BOOKING_BACKFILL',
  'AUX_INFO_SYNC',
  // Live probe + "not configured on the server" gate; the label tracks what the
  // probe found (Configure vs Reconfigure, Create vs Update).
  'DNS_CNAME_RECORD',
  'TWILIO_WEBHOOK_PUSH_CONFIG',
  'TWILIO_LIMITS_ROLES',
  // Offered only when Jira is configured; re-files / re-checks the ticket.
  'APP_BUILD_ANDROID',
  'APP_BUILD_IOS',
  'PUSH_CERT_REQUEST',
  // Offered only while the review guest does not exist yet.
  'REVIEW_GUEST_SETUP',
  // Offered only once the operator has a fleet system to sync from.
  'FLEET_VEHICLES_REGISTER',
]);

/** True when this action's own control owns the execute click (see above). */
export function ownsItsExecute(action: string): boolean {
  return ACTIONS_OWNING_EXECUTE.has(action);
}
